// The OT form's break start checklist: what is ticked by default, and what an entry sends.
// The times themselves come from the backend's OT break rule (GET /officers/ot-break-starts).
import type { OTBreakStartOption, OTBreakStarts } from '@/types/api';

/** The ordinary starts the rule allows — ticked by default. */
export function defaultBreakStarts(options: OTBreakStartOption[] | undefined): Set<string> {
  return new Set((options ?? []).filter(o => o.kind === 'normal').map(o => o.time));
}

/**
 * What to send as an entry's `break_starts`: nothing while the user has left the defaults
 * alone (the backend applies the same rule), otherwise exactly the ticked starts — ordinary
 * ones, plus any "blocked" start the user chose to tick anyway.
 * The last break is not in here — it is `allow_3h_last_break`.
 */
export function breakStartsPayload(
  options: OTBreakStartOption[] | undefined, checked: Set<string>,
): string[] | undefined {
  const tickable = (options ?? []).filter(o => o.kind !== 'last');
  const ticked = tickable.filter(o => checked.has(o.time)).map(o => o.time);
  const normal = tickable.filter(o => o.kind === 'normal');
  const isDefault = normal.every(o => checked.has(o.time)) && ticked.length === normal.length;
  return isDefault ? undefined : ticked;
}

/** True when the checklist leaves the officer no break time at all. */
export function noBreakTimeTicked(breakStarts: OTBreakStarts | null, checked: Set<string>, lastBreakOn: boolean): boolean {
  if (!breakStarts) return false;
  const { options } = breakStarts;
  return options.some(o => o.kind === 'normal')
    && !options.some(o => (o.kind !== 'last' && checked.has(o.time)) || (o.kind === 'last' && lastBreakOn));
}
